/**
 * El dorsal.
 *
 * **Es solo personalizacion.** El numero que llevas en la espalda no te hace
 * mejor ni peor: no toca velocidad, ni vision, ni pase, ni definicion. Es una
 * camiseta, y punto.
 *
 * Antes no era asi. Habia una tabla de arquetipos con nombres de futbolistas
 * reales —el 10 era "Messi" y te subia vision y regate, el 9 era "Haaland" y te
 * subia definicion— aplicada al humano con `dorsalScale = 1`. Dos problemas, y
 * el segundo es el que importa:
 *
 *  1. Repartia nombres y atributos de personas reales, que son suyos.
 *  2. Convertia una eleccion de aspecto en una eleccion de PODER. En cuanto un
 *     numero juega mejor que otro, ya no eliges el que te gusta: eliges el que
 *     gana. Y una pantalla de personalizacion que en realidad es una pantalla
 *     de stats miente sobre lo que esta pidiendo.
 *
 * Lo que queda del numero es lo unico que de verdad es del numero: el 1 es el
 * portero y los centrales llevan 3 y 4. Eso no es una habilidad, es como se
 * reparten los dorsales en un equipo.
 */
import type { Player, TacticalRole } from "./types.js";
import { ROLE_PROFILES, type RoleProfile } from "./tactics.js";

/**
 * Peso al sortear quien se queda cubriendo la meta sin portero fijo.
 *
 * No es un atributo del jugador: es la convencion de numeracion. El 1 es del
 * portero en todos los campos del mundo, y el 3 y el 4 son de los centrales, de
 * modo que si hay que dejar a alguien atras se mira primero ahi. Un jugador con
 * el 1 no para mejor; simplemente es al que le toca.
 */
const SHIRT_GOAL_COVER: Readonly<Record<number, number>> = {
  1: 2.5,
  3: 1.4,
  4: 1.4,
};

/** Peso extra al sortear quien cubre la porteria sin arquero fijo. */
export function dorsalGoalCoverWeight(shirtNumber: number): number {
  return SHIRT_GOAL_COVER[shirtNumber] ?? 0;
}

/**
 * Perfil tactico de un jugador: lo decide su ROL, nada mas.
 *
 * Se mantiene como funcion —y no como un acceso directo a `ROLE_PROFILES`—
 * porque es el sitio donde alguien buscaria "de donde salen las habilidades", y
 * conviene que ahi ponga negro sobre blanco que el dorsal no entra.
 */
export function playerRoleProfile(player: Pick<Player, "tacticRole">): RoleProfile {
  return ROLE_PROFILES[player.tacticRole];
}

/** Dorsales tipicos por rol, para repartirlos con sentido dentro del equipo. */
export const PREFERRED_SHIRTS_BY_ROLE: Record<TacticalRole, readonly number[]> = {
  gk: [1, 13, 25],
  cb: [4, 3, 15, 2],
  fb: [2, 6, 12, 14],
  cm: [5, 10, 8, 16, 18],
  wm: [7, 11, 17, 19],
  st: [7, 9, 10, 18, 21, 19],
};
